import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ResumeUploadZone } from '@/components/candidates/ResumeUploadZone';
import { KanbanBoard, PipelineKanbanBoard } from '@/components/kanban';
import { useCandidates } from '@/hooks/useCandidates';
import { useAuth } from '@/contexts/AuthContext';
import { CandidateStatus, CandidateFilters, CandidateSummary, UserRole } from '@recruitai/shared';
import { AppLayout } from '@/components/AppLayout';
import { apiClient } from '@/lib/api-client';
import { pipelineApi } from '@/lib/pipeline-api';
import { Trash2, List, LayoutGrid } from 'lucide-react';

const STATUS_CONFIG: Record<CandidateStatus, { label: string; variant: 'warning' | 'default' | 'success' | 'destructive' | 'secondary' }> = {
  [CandidateStatus.PENDING]: { label: 'Pendente', variant: 'warning' },
  [CandidateStatus.ANALYZING]: { label: 'Analisando', variant: 'default' },
  [CandidateStatus.APPROVED]: { label: 'Aprovado', variant: 'success' },
  [CandidateStatus.REJECTED]: { label: 'Rejeitado', variant: 'destructive' },
  [CandidateStatus.INTERVIEWED]: { label: 'Entrevistado', variant: 'secondary' },
  [CandidateStatus.HIRED]: { label: 'Contratado', variant: 'success' },
};

export default function CandidateListPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');
  const [statusFilter, setStatusFilter] = useState<CandidateStatus | undefined>();
  const [minScore, setMinScore] = useState<string>('');
  const [maxScore, setMaxScore] = useState<string>('');
  const [showUpload, setShowUpload] = useState(false);

  const isReadOnly = user?.role === UserRole.VIEWER;

  const filters: CandidateFilters = {
    status: statusFilter,
    minScore: minScore ? Number(minScore) : undefined,
    maxScore: maxScore ? Number(maxScore) : undefined,
  };

  const { candidates, counts, isLoading, error, refetch } = useCandidates(jobId!, filters);

  // Fetch pipeline config for the job
  const [pipelineConfig, setPipelineConfig] = useState<import('@recruitai/shared').PipelineConfig | null>(null);
  useEffect(() => {
    if (jobId) {
      pipelineApi.getPipelineConfig(jobId).then(config => {
        if (config?.isCustom) setPipelineConfig(config);
      }).catch(() => {});
    }
  }, [jobId]);

  const handleStageChange = async (candidateId: string, toStageId: string): Promise<boolean> => {
    try {
      await pipelineApi.moveCandidateStage(jobId!, candidateId, toStageId);
      refetch();
      return true;
    } catch {
      return false;
    }
  };

  const handleDeleteCandidate = async (e: React.MouseEvent, candidateId: string, name: string) => {
    e.stopPropagation();
    if (!confirm(`Tem certeza que deseja excluir o candidato "${name}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await apiClient.delete(`/jobs/${jobId}/candidates/${candidateId}`);
      toast.success('Candidato excluído com sucesso');
      refetch();
    } catch {
      toast.error('Erro ao excluir candidato');
    }
  };

  const handleUploadComplete = (newCandidates: CandidateSummary[]) => {
    setShowUpload(false);
    toast.success(`${newCandidates.length} currículo${newCandidates.length !== 1 ? 's' : ''} enviado${newCandidates.length !== 1 ? 's' : ''} com sucesso!`);
    refetch();
  };

  const sorted = [...candidates].sort((a, b) => (b.fitScore ?? 0) - (a.fitScore ?? 0));

  const handleStatusChange = async (candidateId: string, newStatus: CandidateStatus): Promise<boolean> => {
    try {
      await apiClient.patch(`/jobs/${jobId}/candidates/${candidateId}/status`, { status: newStatus });
      refetch();
      return true;
    } catch {
      return false;
    }
  };

  return (
    <AppLayout
      actions={
        <Button variant="outline" onClick={() => navigate(`/jobs/${jobId}`)}>Voltar à Vaga</Button>
      }
    >
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-3xl font-bold mb-1">Candidatos</h2>
              <p className="text-muted-foreground">Gerencie os candidatos desta vaga</p>
            </div>
            <div className="flex border rounded-md">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="rounded-r-none"
              >
                <List className="h-4 w-4 mr-1" /> Lista
              </Button>
              <Button
                variant={viewMode === 'kanban' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('kanban')}
                className="rounded-l-none"
              >
                <LayoutGrid className="h-4 w-4 mr-1" /> Kanban
              </Button>
            </div>
          </div>
          <Button onClick={() => setShowUpload(true)}>+ Upload de Currículos</Button>
        </div>

        {/* Status counters */}
        {counts && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
            {Object.entries(STATUS_CONFIG).map(([status, cfg]) => {
              const count = counts[status as CandidateStatus] ?? 0;
              return (
                <Card
                  key={status}
                  className={`cursor-pointer transition-shadow hover:shadow-md ${statusFilter === status ? 'ring-2 ring-primary' : ''}`}
                  onClick={() => setStatusFilter(statusFilter === status ? undefined : status as CandidateStatus)}
                >
                  <CardContent className="py-3 px-4 text-center">
                    <p className="text-2xl font-bold">{count}</p>
                    <Badge variant={cfg.variant} className="mt-1">{cfg.label}</Badge>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Score filters */}
        <Card className="mb-6">
          <CardHeader><CardTitle className="text-lg">Filtros</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end flex-wrap">
              <div>
                <label className="text-sm font-medium mb-1 block">Score mínimo</label>
                <Input type="number" min={0} max={100} placeholder="0" value={minScore} onChange={e => setMinScore(e.target.value)} className="w-24" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Score máximo</label>
                <Input type="number" min={0} max={100} placeholder="100" value={maxScore} onChange={e => setMaxScore(e.target.value)} className="w-24" />
              </div>
              {(statusFilter || minScore || maxScore) && (
                <Button variant="outline" size="sm" onClick={() => { setStatusFilter(undefined); setMinScore(''); setMaxScore(''); }}>
                  Limpar filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Candidates view */}
        {isLoading ? (
          <div className="text-center py-12"><p className="text-muted-foreground">Carregando candidatos...</p></div>
        ) : error ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-destructive mb-4">Erro ao carregar candidatos: {error.message}</p>
              <Button onClick={() => refetch()}>Tentar Novamente</Button>
            </CardContent>
          </Card>
        ) : sorted.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground mb-4">Nenhum candidato encontrado</p>
              <Button onClick={() => setShowUpload(true)}>Enviar Currículos</Button>
            </CardContent>
          </Card>
        ) : viewMode === 'kanban' ? (
          pipelineConfig ? (
            <PipelineKanbanBoard
              jobId={jobId!}
              candidates={sorted}
              pipelineConfig={pipelineConfig}
              isReadOnly={isReadOnly}
              onStageChange={handleStageChange}
              onCandidateClick={(candidateId) => navigate(`/jobs/${jobId}/candidates/${candidateId}`)}
            />
          ) : (
            <KanbanBoard
              jobId={jobId!}
              candidates={sorted}
              counts={counts || {} as any}
              isReadOnly={isReadOnly}
              onStatusChange={handleStatusChange}
              onCandidateClick={(candidateId) => navigate(`/jobs/${jobId}/candidates/${candidateId}`)}
            />
          )
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Fit Score</TableHead>
                  <TableHead>Data de Upload</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map(c => {
                  const cfg = STATUS_CONFIG[c.status];
                  return (
                    <TableRow
                      key={c.candidateId}
                      className="cursor-pointer"
                      onClick={() => navigate(`/jobs/${jobId}/candidates/${c.candidateId}`)}
                    >
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell>{c.email}</TableCell>
                      <TableCell><Badge variant={cfg.variant}>{cfg.label}</Badge></TableCell>
                      <TableCell className="text-right">
                        {c.fitScore != null ? `${c.fitScore}%` : '—'}
                      </TableCell>
                      <TableCell>{new Date(c.uploadedAt).toLocaleDateString('pt-BR')}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleDeleteCandidate(e, c.candidateId, c.name)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        )}

        {/* Upload dialog */}
        <Dialog open={showUpload} onOpenChange={setShowUpload}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Upload de Currículos</DialogTitle>
            </DialogHeader>
            <ResumeUploadZone
              jobId={jobId!}
              onUploadComplete={handleUploadComplete}
              onCancel={() => setShowUpload(false)}
            />
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
